import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader,
  Container, Badge, Button, Table,
} from "reactstrap";
import { businessesRepo } from "data/repositories/businessesRepo";
import { provincesRepo } from "data/repositories/provincesRepo";
import { businessCategories } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption, toDateLabel } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";

import { categoryLabel } from "labels/tr";

const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "name-asc", label: "Ada göre A-Z" },
  { value: "name-desc", label: "Ada göre Z-A" },
];

export default function AdminBusinesses() {
  const location = useLocation();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [sortBy, setSortBy] = useState("updated-desc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setBusinesses(businessesRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "İşletmeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const provinceOptions = useMemo(
    () => [{ value: "", label: "Tüm şehirler" }, ...provincesRepo.list().map((province) => ({ value: province.id, label: province.name }))],
    []
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = businesses.filter((business) => {
      if (stateFilter === "active" && business.deletedAt) return false;
      if (stateFilter === "archived" && !business.deletedAt) return false;
      if (categoryFilter && business.category !== categoryFilter) return false;
      if (provinceFilter && business.location?.provinceId !== provinceFilter) return false;
      if (!query) return true;

      return [
        business.id,
        business.name,
        business.description,
        business.category,
        business.location?.provinceId,
        business.ownerUserId,
        business.contact?.phone,
        business.contact?.email,
        business.contact?.website,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return sortByOption(next, sortBy);
  }, [businesses, categoryFilter, provinceFilter, search, sortBy, stateFilter]);

  const handleArchive = (business) => {
    if (!confirmAdminAction(business.name)) return;

    try {
      businessesRepo.remove(business.id);
      setFlash({ color: "success", message: `${business.name} arşive alındı.` });
      load();
    } catch (removeError) {
      setFlash({ color: "danger", message: removeError?.message || "İşletme arşivlenemedi." });
    }
  };

  const handleRestore = (business) => {
    try {
      businessesRepo.restore(business.id);
      setFlash({ color: "success", message: `${business.name} tekrar yayına alındı.` });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "İşletme geri alınamadı." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStateFilter("active");
    setCategoryFilter("");
    setProvinceFilter("");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        title="Yönetim — İşletmeler"
        description="İşletme kayıtları mobil uygulama işletme modeliyle uyumlu olarak yönetilir."
        primaryTo={{ tag: Link, to: `/admin${adminPaths.admin.createBusiness}` }}
        primaryLabel="Yeni işletme"
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          <CardHeader>
            <h3 className="mb-3">Tüm İşletmeler</h3>
            <AdminListControls
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Ad, owner, kategori veya iletişim ara"
              filters={[
                {
                  key: "state",
                  label: "Kayıt durumu",
                  value: stateFilter,
                  onChange: setStateFilter,
                  options: [
                    { value: "active", label: "Aktif" },
                    { value: "archived", label: "Arşiv" },
                    { value: "all", label: "Tümü" },
                  ],
                },
                {
                  key: "category",
                  label: "Kategori",
                  value: categoryFilter,
                  onChange: setCategoryFilter,
                  options: [{ value: "", label: "Tüm kategoriler" }, ...businessCategories.map((category) => ({ value: category, label: categoryLabel(category) }))],
                },
                {
                  key: "province",
                  label: "Şehir",
                  value: provinceFilter,
                  onChange: setProvinceFilter,
                  options: provinceOptions,
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={sortOptions}
              onReset={resetFilters}
              resultCount={filtered.length}
              totalCount={businesses.length}
            />
          </CardHeader>
          <CardBody>
            <AdminStateBlock
              flash={flash}
              onDismissFlash={() => setFlash(null)}
              error={error}
              onDismissError={() => setError("")}
              loading={loading}
              spinnerColor="dark"
              isEmpty={filtered.length === 0}
              emptyTitle="İşletme bulunamadı"
            >
              <Table className="align-items-center" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>İşletme</th>
                    <th>Kategori</th>
                    <th>Şehir</th>
                    <th>İletişim</th>
                    <th>Durum</th>
                    <th>Güncellendi</th>
                    <th className="text-right" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <div className="font-weight-bold"><Link to={`/admin/businesses/${b.id}`}>{b.name}</Link></div>
                        <div className="small text-muted">{b.id}</div>
                        <div className="small text-muted">{b.ownerUserId || "-"}</div>
                      </td>
                      <td><Badge color="info">{categoryLabel(b.category)}</Badge></td>
                      <td>{b.location?.provinceId || "-"}</td>
                      <td>
                        <div className="small">{b.contact?.phone || "-"}</div>
                        <div className="small text-muted">{b.contact?.email || b.contact?.website || "-"}</div>
                      </td>
                      <td>
                        <Badge color={b.deletedAt ? "warning" : "success"} pill>
                          {b.deletedAt ? "Arşivde" : "Aktif"}
                        </Badge>
                      </td>
                      <td>{toDateLabel(b.updatedAt)}</td>
                      <td>
                        <div className="d-flex justify-content-end">
                          <Button color="primary" size="sm" className="mr-2" tag={Link} to={`/admin${adminPaths.admin.editBusiness(b.id)}`}>
                            Düzenle
                          </Button>
                          {b.deletedAt ? (
                            <Button color="success" outline size="sm" onClick={() => handleRestore(b)}>
                              Geri al
                            </Button>
                          ) : (
                            <Button color="danger" size="sm" onClick={() => handleArchive(b)}>
                              Arşivle
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </AdminStateBlock>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
